import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Badge, Button, IconButton, Tooltip } from "@mui/material";
import {
  InfoOutlined,
  ModeEditOutlineTwoTone,
  ForumOutlined,
  AttachFileOutlined,
  Add,
  Refresh,
} from "@mui/icons-material";
import ReactHtmlParser from "html-react-parser";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyGetFeedbackListQuery } from "store/services/feedbackApi";

import { useAppMeta } from "hooks";
import ParamsUserAutocomplete from "components/Shared/ParamsUserAutocomplete";

const PRIORITY_INDICATOR = {
  Low: "bg-yellow-500 text-yellow-50",
  Medium: "bg-green-500 text-green-50",
  High: "bg-orange-500 text-orange-50",
  Critical: "bg-red-500 text-red-50",
};

const FeedbackListScreen = () => {
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Open";
  const keyword = searchParams.get("search") || "";
  const responsible = searchParams.get("responsible") || null;

  const breadcrumbs = [{ title: "Feedbacks" }];

  // RTK Query
  const [
    fetchFeedbacks,
    {
      data: feedbackList,
      isLoading: loadingFeedbacks,
      isFetching: fetchingFeedbacks,
      error,
    },
  ] = useLazyGetFeedbackListQuery();

  const feedbackCounts = React.useMemo(() => {
    if (!feedbackList || !feedbackList.length) return;

    const counts = {};

    for (const feedback of feedbackList) {
      counts[feedback?.stage] = counts[feedback?.stage]
        ? counts[feedback?.stage] + 1
        : 1;
    }

    return counts;
  }, [feedbackList]);

  const feedbackListTabs = [
    {
      label: "Open",
      value: "Open",
      badgeCount: feedbackCounts && (feedbackCounts["Open"] ?? 0),
    },
    {
      label: "In Progress",
      value: "In Progress",
      badgeCount: feedbackCounts && (feedbackCounts["In Progress"] ?? 0),
    },
    {
      label: "Verification",
      value: "Verification",
      badgeCount: feedbackCounts && (feedbackCounts["Verification"] ?? 0),
      enablePulse: feedbackCounts && feedbackCounts["Verification"] > 0,
    },
    {
      label: "Closed",
      value: "Closed",
      badgeCount: feedbackCounts && (feedbackCounts["Closed"] ?? 0),
    },
    {
      label: "Discarded",
      value: "Discarded",
      badgeCount: feedbackCounts && (feedbackCounts["Discarded"] ?? 0),
    },
  ];

  // methods
  const getFeedbackResponseTitle = (row) => {
    if (!row?.responses?.length) return "No Responses";

    const _latestResponse = row?.responses?.at(-1);

    return (
      _latestResponse && (
        <>
          <div className="text-sm underline">Last Response: </div>

          <div>{ReactHtmlParser(_latestResponse?.content)}</div>
        </>
      )
    );
  };

  const columns = [
    {
      name: "Title",
      selector: (row) => row?.title ?? "",
      cell: (row) => (
        <Link
          to={`/app/crm/feedback/${row?._id}/detail?prevTab=${activeTab}`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          title={row?.title}
        >
          {row?.title ? (
            <HighlightedText text={row?.title} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
      minWidth: "200px",
    },
    {
      name: "Priority",
      selector: (row) => row?.priority ?? "",
      cell: (row) => (
        <div
          className={twMerge(
            "rounded px-1.5 py-0.5 text-sm",
            PRIORITY_INDICATOR[row?.priority]
          )}
        >
          {row?.priority ?? "N/A"}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Stage",
      selector: (row) => row?.stage ?? "N/A",
    },
    {
      name: "Responsible",
      selector: (row) => row?.responsible?.name ?? "",
      cell: (row) => (
        <div title={row?.responsible?.name} className="line-clamp-2">
          {row?.responsible?.name ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Created At",
      selector: (row) => row?.createdAt ?? "",
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Updated At",
      selector: (row) => row?.updatedAt ?? "",
      cell: (row) => <AppTableDateTime date={row?.updatedAt} />,
    },
    {
      name: "Requested By",
      selector: (row) => row?.createdBy?.name,
      cell: (row) => (
        <div title={row?.createdBy?.name} className="line-clamp-2">
          {row?.createdBy?.name ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex flex-shrink-0 items-center">
          {!!row?.attachments?.length && (
            <IconButton size="small" title="Has attachments">
              <AttachFileOutlined />
            </IconButton>
          )}

          <Tooltip title={getFeedbackResponseTitle(row)} arrow>
            <IconButton size="small">
              <Badge badgeContent={row?.responses?.length} color="primary">
                <ForumOutlined />
              </Badge>
            </IconButton>
          </Tooltip>

          <Link
            to={`/app/crm/feedback/${row?._id}/detail?prevTab=${activeTab}`}
          >
            <IconButton size="small" aria-label="Feedback">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>

          <Link to={`/app/crm/feedback/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Feedback">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  React.useEffect(() => {
    fetchFeedbacks({ ...(responsible && { responsible }) });
  }, [fetchFeedbacks, responsible]);

  useAppMeta(`${activeTab} Feedbacks`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={
        <PageActions isBusy={loadingFeedbacks || fetchingFeedbacks} />
      }
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs
          tabs={feedbackListTabs}
          activeTab={activeTab}
          isBusy={loadingFeedbacks || fetchingFeedbacks}
        >
          <IconButton
            size="small"
            title="Refresh"
            disabled={loadingFeedbacks || fetchingFeedbacks}
            onClick={() => fetchFeedbacks()}
          >
            <Refresh color="primary" />
          </IconButton>
        </CustomTabs>

        <TableFilter filterMineKey="Responsible" searchLabel="Title">
          <ParamsUserAutocomplete
            label="Responsible"
            disabled={loadingFeedbacks || fetchingFeedbacks}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={feedbackList
            ?.filter((feedback) => feedback.stage === activeTab)
            ?.filter(
              (item) =>
                item && item.title.toLowerCase().includes(keyword.toLowerCase())
            )}
          progressPending={loadingFeedbacks || fetchingFeedbacks}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default FeedbackListScreen;

const PageActions = () => {
  return (
    <div className="flex space-x-4">
      <Link to="/app/crm/feedback/create">
        <Button variant="contained" startIcon={<Add />}>
          New Feedback
        </Button>
      </Link>
    </div>
  );
};
